"use client";

import {
	AlertTriangle,
	CheckCircle2,
	Copy,
	Download,
	Loader2,
	Mail,
} from "lucide-react";
import dynamic from "next/dynamic";
import Link from "next/link";
import {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	useTransition,
} from "react";
import { SendToLaptopButton } from "@/components/extension/send-to-laptop-button";
import { ZoneLink } from "@/components/layout/zone-link";
import { buttonClass } from "@/components/ui/button";
import {
	recallSlateContext,
	type SlateContext,
	slateRewardInput,
} from "@/lib/analytics/feed-slate";
import { useAnalytics } from "@/lib/analytics/hooks";
import {
	buildGmailComposeUrl,
	type JobApplyEmailDraft,
} from "@/lib/apply/gmail";
import {
	buildMailtoUrl,
	capBody,
	type HandoffChannel,
	MAILTO_BODY_MAX,
} from "@/lib/apply/handoff";
import { RECRUITER_EMAIL_DRAFT_FLAG } from "@/lib/apply/recruiter-email";
import { reloadOncePerBuild } from "@/lib/error-recovery/reload-budget";
import { useGuestEmailApplyDraftVariant } from "@/lib/experiments/guest-email-apply-draft";
import { isServerActionSkewError } from "@/lib/profile/server-action-skew";
import { buildJobApplyUrl } from "@/lib/sitemap/utils";
import { isTimeoutError, withTimeout } from "@/lib/timeout";
import {
	checkEmailApplyReadiness,
	type EmailApplyReadinessResult,
	type EmailHandoffDraft,
	markEmailApplySent,
	prepareEmailApply,
	recordEmailApplyOutbound,
	reportApplyEmailBounced,
} from "@/server/actions/apply-handoff";

export const PREPARE_TIMEOUT_MS = 60_000;
export const PREPARE_STAGE_LETTER_MS = 8_000;
export const PREPARE_STAGE_SLOW_MS = 30_000;
export const HANDOFF_ACTION_TIMEOUT_MS = 20_000;

/**
 * Apply-by-email handoff CTA — the application leaves the SEEKER's mailbox.
 *
 * Replaces `RecruiterEmailCta` ("we'll email the recruiter for you"), which sent
 * from `hello@lokerdollar.com` and put cold application volume on the same
 * From-domain reputation magic-link login depends on. Here we prepare the whole
 * draft — tailored cover letter, résumé, subject, recipient — and open it in the
 * mail client the seeker already uses. They press send; the employer's reply
 * lands in their inbox directly, with no relay in the middle.
 *
 * The channel picker is not decoration: no URL scheme can attach a file, so the
 * `.eml` download is the only complete one-click path, and every other channel
 * downloads the CV alongside opening the draft. See `src/lib/apply/handoff.ts`.
 */

// Loaded only when the `recruiter-email-draft` flag is on, so the server action
// and its dependencies never ship to (or run for) a seeker who cannot see it.
const RecruiterEmailDraft = dynamic(
	() =>
		import("@/components/jobs/recruiter-email-draft").then(
			(m) => m.RecruiterEmailDraft,
		),
	{ ssr: false },
);

const CHANNEL_STORAGE_KEY = "ld_apply_mail_channel";

const MISSING_FIELD_LABELS: Record<string, { en: string; id: string }> = {
	email: { en: "your email", id: "emailmu" },
	name: { en: "your name", id: "namamu" },
	resume: { en: "your CV", id: "CV-mu" },
};

/** Turns `["name","resume"]` into "your name and your CV" / "namamu dan CV-mu",
 *  falling back to the generic copy when the server didn't name a gap. */
function describeMissing(fields: string[], isEn: boolean): string | null {
	const labels = fields
		.map((f) => MISSING_FIELD_LABELS[f]?.[isEn ? "en" : "id"])
		.filter((v): v is string => Boolean(v));
	if (labels.length === 0) return null;
	if (labels.length === 1) return labels[0];
	const last = labels[labels.length - 1];
	const rest = labels.slice(0, -1).join(", ");
	return isEn ? `${rest} and ${last}` : `${rest} dan ${last}`;
}

type Phase = "idle" | "preparing" | "ready" | "sent" | "error";

/**
 * One readiness answer per page load, shared by every instance.
 *
 * A feed page renders this component once per apply-by-email row, so a naive
 * per-instance mount fetch would be N server actions and N×5 D1 reads for a
 * single page. The answer is job-independent (profile + session only), so one
 * in-flight promise is the whole page's answer.
 */
let readinessRequest: Promise<EmailApplyReadinessResult> | null = null;

function loadReadinessOnce(): Promise<EmailApplyReadinessResult> {
	// `Promise.resolve().then(…)` so a SYNCHRONOUS throw (no network, action
	// unavailable) becomes a rejection the caller's `.catch` handles, instead of
	// blowing up the effect that started it. A pre-check that cannot answer must
	// leave the CTA exactly as it was.
	readinessRequest ??= Promise.resolve().then(() => checkEmailApplyReadiness());
	return readinessRequest;
}

/** Drop the cached answer so the next mount re-asks. Exported for tests, and
 *  used below on a bfcache restore — a seeker who just completed their profile
 *  in another tab/zone must not come back to a stale blocker. */
export function resetApplyReadinessCache(): void {
	readinessRequest = null;
}

type ReadinessState =
	| { status: "unknown" }
	| { status: "ready" }
	| {
			status: "blocked";
			reason: "unauthorized" | "incomplete_profile";
			missing: string[];
	  };

function rememberChannel(channel: HandoffChannel) {
	try {
		window.localStorage.setItem(CHANNEL_STORAGE_KEY, channel);
	} catch {
		// private mode / storage disabled — the picker just won't be pre-ordered.
	}
}

function preferredChannel(): HandoffChannel | null {
	try {
		const v = window.localStorage.getItem(CHANNEL_STORAGE_KEY);
		return v === "gmail" || v === "outlook" || v === "mailto" || v === "eml"
			? v
			: null;
	} catch {
		return null;
	}
}

/**
 * Phone-class device, decided on the client only. The job page is edge-cached,
 * so the server cannot branch on UA (and must not Set-Cookie). Starts `false`
 * so SSR and hydration match; flips in an effect.
 */
function useIsMobileDevice(): boolean {
	const [mobile, setMobile] = useState(false);
	useEffect(() => {
		const uaMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(
			navigator.userAgent,
		);
		const narrowTouch =
			typeof window.matchMedia === "function" &&
			window.matchMedia("(max-width: 767px) and (pointer: coarse)").matches;
		setMobile(uaMobile || narrowTouch);
	}, []);
	return mobile;
}

/**
 * Mobile mailto cannot attach a file and a signed-in CV link would be a
 * non-expiring public URL of private data, so the body tells the employer the
 * CV is available on request instead. Gap: no expiring CV share link exists
 * yet (`/api/apply/handoff/resume` is session-gated).
 */
function cvOnRequestLine(isEn: boolean): string {
	return isEn
		? "My CV is available on request - just reply to this email."
		: "CV saya tersedia atas permintaan - balas saja email ini.";
}

function buildMobileMailtoUrl(
	draft: {
		to: string;
		subject: string;
		body: string;
		ccRelay?: string | null;
	},
	isEn: boolean,
): string {
	const line = cvOnRequestLine(isEn);
	const body = `${capBody(draft.body, MAILTO_BODY_MAX - line.length - 2)}\n\n${line}`;
	// Flag `apply-handoff-cc-relay`: the visible Cc travels on the phone path
	// too, or a mobile seeker's employer replies would stay invisible.
	return buildMailtoUrl({
		to: draft.to,
		subject: draft.subject,
		body,
		...(draft.ccRelay ? { cc: draft.ccRelay } : {}),
	});
}

/** Download a blob/URL without navigating away from the job page. */
function triggerDownload(href: string, filename?: string) {
	const a = document.createElement("a");
	a.href = href;
	if (filename) a.download = filename;
	a.rel = "noopener";
	document.body.appendChild(a);
	a.click();
	a.remove();
}

export function ApplyEmailHandoff({
	jobId,
	locale,
	label,
	variant = "secondary",
	autoPrepareOnReturn = false,
	jobTitle,
	company = null,
	applyEmail = null,
	slate = null,
}: {
	jobId: string;
	/** Feed reward join: the slate this card was served in. A feed row passes
	 *  it; the job page leaves it out and the slate its card click stashed is
	 *  recalled instead (`recallSlateContext`). */
	slate?: SlateContext | null;
	locale: string;
	/** Prepare the draft once, without a click, when the seeker returns from
	 *  login carrying `?handoff=1` (set on the login `callbackURL` below). Pass
	 *  true only on the instance that is actually on screen — the job page mounts
	 *  a desktop and a mobile CTA at once. */
	autoPrepareOnReturn?: boolean;
	/** Overrides the idle-button copy only (feed rows use a more direct label). */
	label?: { en: string; id: string };
	/** `primary` renders the filled apply button — used on job detail for
	 *  email-apply postings, where the handoff IS the apply action and the raw
	 *  Gmail-draft link demotes to a secondary text link. */
	variant?: "primary" | "secondary";
	/**
	 * Job title/company/apply-address — the SAME fields `buildJobApplyGmailUrl`
	 * used server-side to build the `applyUrl` Gmail fallback link
	 * (`src/app/[locale]/(public)/jobs/[slug]/page.tsx`). Used ONLY to build the
	 * `guest-email-apply-draft` `template` variant's ready-to-send email — a
	 * guest who is `unauthorized` has no session for the server action to build
	 * a draft from, so these travel down as props instead. Optional: when
	 * absent (or the variant resolves to `control`), behavior is unchanged
	 * (today's login wall).
	 */
	jobTitle?: string;
	company?: string | null;
	applyEmail?: string | null;
}) {
	const { posthog } = useAnalytics();
	const isEn = locale === "en";
	// Visitor + served slate stamped on the apply receipt (validated server-side
	// by `parseRewardContext`). Read at call time so a late PostHog init counts.
	const rewardInput = useCallback(
		() =>
			slateRewardInput(
				slate ?? recallSlateContext(jobId),
				posthog?.get_distinct_id?.() ?? null,
			),
		[slate, jobId, posthog],
	);
	const [phase, setPhase] = useState<Phase>("idle");
	const [draft, setDraft] = useState<EmailHandoffDraft | null>(null);
	const [errorKind, setErrorKind] = useState<
		"generic" | "incomplete_profile" | "unauthorized" | "timeout"
	>("generic");
	const [missingFields, setMissingFields] = useState<string[]>([]);
	const [opened, setOpened] = useState<HandoffChannel | null>(null);
	const [prepStage, setPrepStage] = useState<0 | 1 | 2>(0);
	const [actionError, setActionError] = useState<
		"eml" | "confirm" | "bounce" | null
	>(null);
	const [copied, setCopied] = useState(false);
	const [readiness, setReadiness] = useState<ReadinessState>({
		status: "unknown",
	});
	const [pending, startTransition] = useTransition();
	const isMobile = useIsMobileDevice();
	// `recruiter-email-draft` is the SOLE switch. null = unknown = off.
	const [recruiterEmailOn, setRecruiterEmailOn] = useState<boolean | null>(
		null,
	);
	useEffect(() => {
		if (phase !== "ready" || !posthog) return;
		const read = () =>
			setRecruiterEmailOn(
				posthog.getFeatureFlag?.(RECRUITER_EMAIL_DRAFT_FLAG) === true,
			);
		read();
		return posthog.onFeatureFlags?.(read);
	}, [phase, posthog]);
	// A/B: guest-email-apply-draft (control | template) — see
	// src/lib/experiments/guest-email-apply-draft.ts. The flag is read only
	// once readiness says "guest, unauthorized", so exposure = guests who saw
	// the wall or the template.
	const guestDraftVariant = useGuestEmailApplyDraftVariant(
		posthog,
		readiness.status === "blocked" && readiness.reason === "unauthorized",
	);
	const [guestTemplateCopied, setGuestTemplateCopied] = useState(false);
	// The "Copied" confirmations clear themselves after 2.5 s. The timers live in
	// effects so unmounting cancels them: a bare setTimeout outlived the
	// component and called setState after teardown (CI: "window is not defined"
	// from react-dom, PR #6861 test-lane 2, 2026-10-14).
	useEffect(() => {
		if (!copied) return;
		const t = setTimeout(() => setCopied(false), 2500);
		return () => clearTimeout(t);
	}, [copied]);
	useEffect(() => {
		if (!guestTemplateCopied) return;
		const t = setTimeout(() => setGuestTemplateCopied(false), 2500);
		return () => clearTimeout(t);
	}, [guestTemplateCopied]);
	const guestTemplateBodyRef = useRef<HTMLTextAreaElement>(null);

	// The `template` variant's ready-to-send email — built entirely client-side
	// (no AI, no server call) from data the page already rendered, via the SAME
	// Gmail/mailto builders the signed-in draft uses. `null` when the caller
	// didn't pass enough to build one (defensive — falls back to the login wall).
	const guestTemplate = useMemo(() => {
		if (!jobTitle || !applyEmail) return null;
		const at = company ? (isEn ? ` at ${company}` : ` di ${company}`) : "";
		const subject = isEn
			? `Application: ${jobTitle} — [Your name]`
			: `Lamaran: ${jobTitle} — [Nama kamu]`;
		const body = isEn
			? `Hi,\n\nI'd like to apply for the ${jobTitle} role${at}, which I saw on lokerdollar.com.\n\n[Add a sentence or two about yourself and why you're a fit for this role.]\n\nCV: [CV link or attach your CV]\n\nThanks,\n[Your name]`
			: `Halo,\n\nSaya ingin melamar posisi ${jobTitle}${at} yang saya lihat di lokerdollar.com.\n\n[Tambahkan satu-dua kalimat tentang dirimu dan kenapa cocok untuk posisi ini.]\n\nCV: [link CV / lampirkan CV]\n\nTerima kasih,\n[Nama kamu]`;
		const emailDraft: JobApplyEmailDraft = { to: applyEmail, subject, body };
		return {
			to: applyEmail,
			subject,
			body,
			gmailUrl: buildGmailComposeUrl(emailDraft),
			mailtoUrl: buildMailtoUrl(emailDraft),
		};
	}, [jobTitle, company, applyEmail, isEn]);

	const onOpenGuestTemplateChannel = useCallback(
		(channel: "gmail" | "mailto") => () => {
			posthog?.capture("apply_email_handoff_opened", {
				job_id: jobId,
				channel,
				guest_variant: "template",
			});
		},
		[jobId, posthog],
	);

	const onCopyGuestTemplate = useCallback(async () => {
		if (!guestTemplate) return;
		try {
			await navigator.clipboard.writeText(
				`${guestTemplate.to}\n\n${guestTemplate.subject}\n\n${guestTemplate.body}`,
			);
			setGuestTemplateCopied(true);
			posthog?.capture("apply_email_handoff_opened", {
				job_id: jobId,
				channel: "copy",
				guest_variant: "template",
			});
		} catch {
			// Clipboard permission denied/unavailable — fall back to selecting the
			// read-only body textarea so the seeker can copy it manually.
			setGuestTemplateCopied(false);
			guestTemplateBodyRef.current?.select();
		}
	}, [guestTemplate, jobId, posthog]);

	const onPrepare = useCallback(() => {
		posthog?.capture("apply_email_handoff_cta_clicked", {
			job_id: jobId,
			locale,
		});
		setPhase("preparing");
		startTransition(async () => {
			// The session id travels with the click so the server-side
			// `prepared`/`blocked` events can be joined back to it — without it the
			// funnel reads `sessions = 0` on every server step.
			let r: Awaited<ReturnType<typeof prepareEmailApply>>;
			try {
				r = await withTimeout("apply_email_prepare", PREPARE_TIMEOUT_MS, () =>
					prepareEmailApply(
						jobId,
						locale,
						posthog?.get_session_id?.(),
						"cta",
						rewardInput(),
					),
				);
			} catch (err) {
				const timedOut = isTimeoutError(err);
				// `prepareEmailApply` never rejects by contract, so a client-side
				// throw is almost always a stale Server Action id after a deploy
				// (PostHog: 4 of 5 `prepare_failed` in 14d were "threw",
				// 2026-10-14..21). Tag it, then reload once onto the new build.
				const skew = !timedOut && isServerActionSkewError(err);
				posthog?.capture("apply_email_handoff_prepare_failed", {
					job_id: jobId,
					locale,
					reason: timedOut ? "timeout" : skew ? "skew" : "threw",
				});
				if (skew && reloadOncePerBuild("action")) return;
				setMissingFields([]);
				setErrorKind(timedOut ? "timeout" : "generic");
				setPhase("error");
				return;
			}
			if (r.ok) {
				setDraft(r.draft);
				setPhase("ready");
				return;
			}
			setMissingFields(
				r.error === "incomplete_profile" ? (r.missing ?? []) : [],
			);
			setErrorKind(
				r.error === "incomplete_profile"
					? "incomplete_profile"
					: r.error === "unauthorized"
						? "unauthorized"
						: // "internal" (server caught a throw) and the other blocks share
							// the generic copy plus the original-site fallback link.
							"generic",
			);
			setPhase("error");
		});
	}, [jobId, locale, posthog, rewardInput]);

	/** Open the draft in `channel`, and (for the channels that cannot attach)
	 *  download the résumé so it is sitting in Downloads when they hit Attach. */
	const openIn = useCallback(
		async (channel: HandoffChannel) => {
			if (!draft) return;
			setActionError(null);
			rememberChannel(channel);
			setOpened(channel);
			posthog?.capture("apply_email_handoff_opened", {
				job_id: jobId,
				channel,
				guessed: draft.guessed,
			});
			// Durable server-side twin of the PostHog event above, and the
			// analogue of the `/out` hop for a path that never touches `/out`:
			// this is the instant we know the seeker left for their mail client.
			// Without it, `jobs.apply_clicks` and `apply_outbound` never move for
			// an apply-by-email job, so every metric built on them silently
			// excludes that whole class of listing. Fire-and-forget — the action
			// swallows its own failures and must never block the handoff.
			void recordEmailApplyOutbound(jobId, locale, channel).catch(
				() => undefined,
			);

			if (channel === "eml") {
				try {
					const blob = await withTimeout(
						"apply_email_eml",
						HANDOFF_ACTION_TIMEOUT_MS,
						async (signal) => {
							const res = await fetch(draft.emlEndpoint, {
								method: "POST",
								headers: { "content-type": "application/json" },
								body: JSON.stringify({
									jobId,
									subject: draft.subject,
									body: draft.body,
								}),
								signal,
							});
							if (!res.ok) throw new Error(`eml_http_${res.status}`);
							return res.blob();
						},
					);
					const blobUrl = URL.createObjectURL(blob);
					triggerDownload(blobUrl, "application.eml");
					setTimeout(() => URL.revokeObjectURL(blobUrl), 30_000);
				} catch {
					// Keep the draft on screen: the other channels still work.
					setActionError("eml");
				}
				return;
			}

			if (channel === "mailto" && isMobile) {
				// Phone: hand the draft to the OS mail app in this tab (a `_blank`
				// mailto leaves a dead tab), with the CV-on-request line, and skip
				// the CV download — there is nothing to attach it to.
				posthog?.capture("apply_email_handoff_mailto_mobile_clicked", {
					job_id: jobId,
					locale,
				});
				window.location.href = buildMobileMailtoUrl(draft, isEn);
				return;
			}

			const url =
				channel === "gmail"
					? draft.urls.gmail
					: channel === "outlook"
						? draft.urls.outlook
						: draft.urls.mailto;
			window.open(url, "_blank", "noopener,noreferrer");
			if (draft.resumeUrl && !isMobile) triggerDownload(draft.resumeUrl);
		},
		[draft, jobId, locale, posthog, isMobile, isEn],
	);

	const onCopyBody = useCallback(async () => {
		if (!draft) return;
		try {
			await navigator.clipboard.writeText(
				`${draft.to}\n\n${draft.subject}\n\n${draft.body}`,
			);
			setCopied(true);
		} catch {
			setCopied(false);
		}
	}, [draft]);

	const onConfirmSent = useCallback(() => {
		setActionError(null);
		startTransition(async () => {
			try {
				const r = await withTimeout(
					"apply_email_mark_sent",
					HANDOFF_ACTION_TIMEOUT_MS,
					() => markEmailApplySent(jobId, opened ?? "copy", rewardInput()),
				);
				if (r.ok) {
					setPhase("sent");
					return;
				}
			} catch {
				// Falls through to the visible error below.
			}
			setActionError("confirm");
		});
	}, [jobId, opened, rewardInput]);

	const onReportBounce = useCallback(() => {
		setActionError(null);
		startTransition(async () => {
			try {
				const r = await withTimeout(
					"apply_email_bounce",
					HANDOFF_ACTION_TIMEOUT_MS,
					() => reportApplyEmailBounced(jobId),
				);
				if (r.ok) {
					setPhase("sent");
					return;
				}
			} catch {
				// Falls through to the visible error below.
			}
			setActionError("bounce");
		});
	}, [jobId]);

	// "Complete profile →" (below, `errorKind === "incomplete_profile"`) sends
	// the seeker to `/settings`, which lives in the `apps/app` zone — a
	// SEPARATELY DEPLOYED worker (apps/CLAUDE.md), so returning here is a real
	// browser navigation, never a Next.js soft nav. Pressing Back after
	// finishing the upload can restore this component from the browser's
	// back/forward cache instead of remounting it, which replays whatever
	// `phase` it was frozen in — the pre-upload "incomplete_profile" error —
	// without ever re-checking readiness. The seeker lands back on the job
	// re-blocked by the exact gate they just cleared and, seeing no live
	// button to retry, abandons (P0, confirmed via session replay). `pageshow`
	// + `event.persisted` is the browser's own signal for a bfcache restore;
	// reset a frozen error back to idle so the primary button reappears and
	// the next click re-checks the now-current profile instead of the stale
	// snapshot.
	useEffect(() => {
		const onPageShow = (event: PageTransitionEvent) => {
			if (!event.persisted) return;
			if (phase === "error") setPhase("idle");
			// The profile may have been completed in the meantime — the whole
			// reason the seeker left this page. Re-ask instead of restoring the
			// blocker the browser froze.
			resetApplyReadinessCache();
			setReadiness({ status: "unknown" });
		};
		window.addEventListener("pageshow", onPageShow);
		return () => window.removeEventListener("pageshow", onPageShow);
	}, [phase]);

	/**
	 * Ask the readiness gate BEFORE drawing the CTA. Measured 2026-09-02: 24
	 * users pressed this button in 14 days and 4 reached a draft — the gate ran
	 * only after the click, so the other 20 got a button that turned into a
	 * disabled "Menyiapkan…" and then an easy-to-miss line of hint text
	 * (`$dead_click`, replay 01a0508f-…). A blocker that names the missing
	 * field and links to the fix is the difference between a dead end and a
	 * next step.
	 *
	 * A failed/pending check leaves `status: "unknown"`, which keeps the CTA —
	 * never let a telemetry-grade pre-check remove the apply button.
	 */
	useEffect(() => {
		if (readiness.status !== "unknown") return;
		let cancelled = false;
		loadReadinessOnce()
			.then((r) => {
				if (cancelled) return;
				setReadiness(
					r.ready
						? { status: "ready" }
						: { status: "blocked", reason: r.reason, missing: r.missing },
				);
			})
			.catch(() => undefined);
		return () => {
			cancelled = true;
		};
	}, [readiness.status]);

	// Returning from login: the guest tapped "Email to apply", signed in, and was
	// sent back with `?handoff=1`. Prepare the draft for them so the tap they
	// already made is not lost. Only `onPrepare` runs — opening a mail client is
	// still their own click, so no popup blocker is involved.
	useEffect(() => {
		if (!autoPrepareOnReturn) return;
		if (readiness.status !== "ready" || phase !== "idle") return;
		const params = new URLSearchParams(window.location.search);
		if (params.get("handoff") !== "1") return;
		// Stripping the param IS the once-only guard: the job page mounts two CTAs
		// (desktop + mobile), and the second one's effect finds nothing to act on.
		params.delete("handoff");
		const query = params.toString();
		window.history.replaceState(
			null,
			"",
			`${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`,
		);
		onPrepare();
	}, [autoPrepareOnReturn, readiness.status, phase, onPrepare]);

	useEffect(() => {
		if (phase !== "preparing") {
			setPrepStage(0);
			return;
		}
		const toLetter = setTimeout(() => setPrepStage(1), PREPARE_STAGE_LETTER_MS);
		const toSlow = setTimeout(() => setPrepStage(2), PREPARE_STAGE_SLOW_MS);
		return () => {
			clearTimeout(toLetter);
			clearTimeout(toSlow);
		};
	}, [phase]);

	if (phase === "sent") {
		return (
			<div
				data-testid="apply-email-handoff-sent"
				className="flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2.5 text-sm text-foreground"
			>
				<CheckCircle2
					className="h-4 w-4 shrink-0 text-primary"
					aria-hidden="true"
				/>
				<span className="flex-1">
					{isEn ? (
						<>
							Application tracked.{" "}
							<ZoneLink
								href={`/${locale}/applications`}
								className="font-medium text-primary underline-offset-4 hover:underline"
							>
								Track it →
							</ZoneLink>
						</>
					) : (
						<>
							Lamaran tercatat.{" "}
							<ZoneLink
								href={`/${locale}/applications`}
								className="font-medium text-primary underline-offset-4 hover:underline"
							>
								Pantau →
							</ZoneLink>
						</>
					)}
				</span>
			</div>
		);
	}

	if (phase === "error") {
		const missingDesc = describeMissing(missingFields, isEn);
		return (
			<div
				data-testid="apply-email-handoff-error"
				className="flex flex-col gap-1.5 text-xs text-muted-foreground"
			>
				<span>
					{errorKind === "incomplete_profile"
						? isEn
							? `Add ${missingDesc ?? "your name, email, and CV"} so we can prepare the application email.`
							: `Lengkapi ${missingDesc ?? "nama, email, dan CV"} agar kami bisa menyiapkan email lamaranmu.`
						: errorKind === "unauthorized"
							? isEn
								? "Please sign in to use this."
								: "Masuk untuk menggunakan fitur ini."
							: errorKind === "timeout"
								? isEn
									? "Preparing your email took over a minute. Nothing was sent. Try again."
									: "Menyiapkan emailmu lebih dari satu menit. Belum ada yang terkirim. Coba lagi."
								: isEn
									? "Couldn't prepare the email. Try again."
									: "Gagal menyiapkan email. Coba lagi."}
				</span>
				<div className="flex flex-wrap gap-2">
					{errorKind === "incomplete_profile" ? (
						<ZoneLink
							href={`/${locale}/settings`}
							className="font-medium text-primary underline-offset-4 hover:underline"
						>
							{isEn ? "Complete profile →" : "Lengkapi profil →"}
						</ZoneLink>
					) : null}
					{errorKind === "unauthorized" ? (
						<Link
							href={`/${locale}/login?callbackURL=/${locale}/jobs/${jobId}`}
							className="font-medium text-primary underline-offset-4 hover:underline"
						>
							{isEn ? "Log in →" : "Masuk →"}
						</Link>
					) : null}
					<button
						type="button"
						onClick={() => {
							if (errorKind === "generic" || errorKind === "timeout")
								onPrepare();
							else setPhase("idle");
						}}
						className="rounded font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
					>
						{isEn ? "Try again" : "Coba lagi"}
					</button>
					{errorKind === "generic" || errorKind === "timeout" ? (
						<a
							href={buildJobApplyUrl({
								jobId,
								title: jobTitle ?? "",
								company: company ?? null,
								locale,
							})}
							target="_blank"
							rel="noopener"
							data-testid="apply-email-handoff-fallback"
							className="rounded font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
						>
							{isEn
								? "Apply on the original site →"
								: "Lamar di situs aslinya →"}
						</a>
					) : null}
				</div>
			</div>
		);
	}

	if (phase === "ready" && draft) {
		const preferred = preferredChannel();
		const channelOptions: Array<{
			id: HandoffChannel;
			en: string;
			id_: string;
		}> = [
			{ id: "gmail", en: "Gmail", id_: "Gmail" },
			{
				id: "eml",
				en: "Mail app (with CV attached)",
				id_: "Aplikasi email (CV terlampir)",
			},
			{ id: "mailto", en: "Default mail app", id_: "Aplikasi email bawaan" },
			{ id: "outlook", en: "Outlook web", id_: "Outlook web" },
		];
		// The seeker's last choice floats to the top — most people use one mail
		// client forever, and re-picking it on every application is pure friction.
		// On a phone the OS mail app is the only path that works end to end
		// (no extension, no .eml handler), so `mailto` is pinned first.
		const channels = isMobile
			? [
					{
						id: "mailto" as const,
						en: "Mail app (recommended on phone)",
						id_: "Aplikasi email (disarankan di HP)",
					},
					...channelOptions.filter((c) => c.id !== "mailto" && c.id !== "eml"),
				]
			: [...channelOptions].sort((a, b) =>
					a.id === preferred ? -1 : b.id === preferred ? 1 : 0,
				);

		return (
			<div
				data-testid="apply-email-handoff-ready"
				className="flex flex-col gap-3 rounded-xl border border-border bg-card px-3 py-3 text-sm text-card-foreground"
			>
				<div className="flex flex-col gap-1">
					<span className="font-semibold text-foreground">
						{isEn
							? "Your application is ready — send it from your own email"
							: "Lamaranmu siap — kirim dari emailmu sendiri"}
					</span>
					<span className="text-xs text-muted-foreground">
						{isEn
							? "It goes straight from your inbox to the employer, so their reply comes back to you."
							: "Terkirim langsung dari inbox-mu ke pemberi kerja, jadi balasannya masuk ke kamu."}
					</span>
				</div>

				<div className="flex flex-col gap-1 rounded-lg bg-muted px-2.5 py-2 text-xs">
					<span className="text-muted-foreground">
						{isEn ? "To" : "Ke"}:{" "}
						<span className="font-mono text-foreground">{draft.to}</span>
					</span>
					{draft.guessed ? (
						<span className="flex items-start gap-1.5 text-muted-foreground">
							<AlertTriangle
								className="mt-0.5 h-3.5 w-3.5 shrink-0"
								aria-hidden="true"
							/>
							<span>
								{isEn
									? "Company careers inbox — we derived it from their domain. Check it before you send."
									: "Inbox karier perusahaan — kami turunkan dari domain mereka. Cek dulu sebelum kirim."}
								{draft.alternates.length > 0 ? (
									<>
										{" "}
										{isEn ? "Alternatives:" : "Alternatif:"}{" "}
										<span className="font-mono">
											{draft.alternates.slice(0, 3).join(", ")}
										</span>
									</>
								) : null}
							</span>
						</span>
					) : (
						<span className="text-muted-foreground">
							{isEn
								? "Applications inbox taken from the posting."
								: "Inbox lamaran diambil dari lowongan."}
						</span>
					)}
					{draft.ccRelay ? (
						<span
							data-testid="apply-email-handoff-cc-note"
							className="text-muted-foreground"
						>
							{isEn
								? "LokerDollar is on CC so the employer's reply is logged in your tracker."
								: "LokerDollar ada di CC supaya balasan perusahaan tercatat di trackermu."}
						</span>
					) : null}
				</div>

				<fieldset className="flex flex-col gap-1.5">
					<legend className="mb-1 text-xs font-medium text-muted-foreground">
						{isEn ? "Open in" : "Buka di"}
					</legend>
					{channels.map((c) => (
						<button
							key={c.id}
							type="button"
							onClick={() => void openIn(c.id)}
							data-testid={`apply-email-handoff-open-${c.id}`}
							className="flex items-center justify-between gap-2 rounded-lg border border-border bg-background px-3 py-2 text-left text-sm font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
						>
							<span>{isEn ? c.en : c.id_}</span>
							{c.id === "eml" ? (
								<Download className="h-4 w-4 text-primary" aria-hidden="true" />
							) : (
								<Mail className="h-4 w-4 text-primary" aria-hidden="true" />
							)}
						</button>
					))}
				</fieldset>

				{isMobile ? (
					<div className="flex flex-col gap-1.5 rounded-lg bg-muted px-2.5 py-2 text-xs text-muted-foreground">
						<span>
							{isEn
								? "A phone mail app can't attach your CV, so the email says it is available on request. Want to attach it? Finish this on a laptop."
								: "Aplikasi email di HP tidak bisa melampirkan CV, jadi emailnya menyebut CV tersedia atas permintaan. Mau melampirkan? Selesaikan di laptop."}
						</span>
						<div className="self-start">
							<SendToLaptopButton
								locale={locale}
								surface="apply_email_handoff"
								jobId={jobId}
								draft={{
									to: draft.to,
									subject: draft.subject,
									body: draft.body,
								}}
							/>
						</div>
					</div>
				) : null}

				{!isMobile && opened && opened !== "eml" && draft.resumeUrl ? (
					<p className="text-xs text-muted-foreground">
						{isEn
							? "We downloaded your CV — attach it in the draft before sending."
							: "CV-mu sudah diunduh — lampirkan di draft sebelum kirim."}
					</p>
				) : null}

				<div className="flex flex-wrap items-center gap-3 text-xs">
					<button
						type="button"
						onClick={() => void onCopyBody()}
						className="flex items-center gap-1 rounded font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
					>
						<Copy className="h-3.5 w-3.5" aria-hidden="true" />
						{copied
							? isEn
								? "Copied"
								: "Tersalin"
							: isEn
								? "Copy the email"
								: "Salin email"}
					</button>
					<button
						type="button"
						onClick={onConfirmSent}
						disabled={pending}
						data-testid="apply-email-handoff-confirm"
						className="rounded bg-primary px-3 py-1.5 font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
					>
						{isEn ? "I sent it" : "Sudah kirim"}
					</button>
					{draft.guessed ? (
						<button
							type="button"
							onClick={onReportBounce}
							disabled={pending}
							className="rounded text-muted-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
						>
							{isEn ? "It bounced" : "Emailnya bounce"}
						</button>
					) : null}
				</div>
				{actionError ? (
					<p
						role="alert"
						data-testid="apply-email-handoff-action-error"
						className="text-xs text-destructive"
					>
						{actionError === "eml"
							? isEn
								? "Couldn't build the .eml file. Nothing was sent. Pick another option above."
								: "File .eml gagal dibuat. Belum ada yang terkirim. Pilih opsi lain di atas."
							: actionError === "confirm"
								? isEn
									? "Couldn't record that you sent it. Your email is unaffected. Try again."
									: "Gagal mencatat bahwa kamu sudah kirim. Emailmu tidak terpengaruh. Coba lagi."
								: isEn
									? "Couldn't record the bounce. Try again."
									: "Gagal mencatat bounce. Coba lagi."}
					</p>
				) : null}
				{recruiterEmailOn === true && !draft.guessed ? (
					<RecruiterEmailDraft jobId={jobId} locale={locale} />
				) : null}
			</div>
		);
	}

	// The seeker cannot succeed — show them what to fix instead of a CTA that
	// dead-ends. `phase` is still `idle` here (the earlier branches own every
	// other phase), so this never replaces a prepared draft.
	if (readiness.status === "blocked") {
		const missingDesc = describeMissing(readiness.missing, isEn);
		// Reading `location` during render is safe HERE and only here: readiness
		// starts `unknown`, so this branch can only be reached after the mount
		// effect resolved — never during SSR or hydration.
		// `handoff=1` tells the job page's CTA to prepare the draft on return from
		// login (see the auto-prepare effect above); a feed row ignores it.
		let returnTo = `/${locale}/jobs`;
		if (typeof window !== "undefined") {
			const url = new URL(window.location.href);
			url.searchParams.set("handoff", "1");
			returnTo = `${url.pathname}${url.search}`;
		}
		const signInHref = `/${locale}/login?callbackURL=${encodeURIComponent(returnTo)}`;

		// guest-email-apply-draft `template` — a guest who cannot get the AI
		// draft (no stored résumé/userId, see `checkEmailApplyReadiness`) gets a
		// ready-to-send template for THIS job instead of the login wall below.
		// `control` (or a missing jobTitle/applyEmail) falls through unchanged.
		if (
			readiness.reason === "unauthorized" &&
			guestDraftVariant === "template" &&
			guestTemplate
		) {
			return (
				<div
					data-testid="apply-email-handoff-guest-template"
					className="flex flex-col gap-3 rounded-xl border border-border bg-card px-3 py-3 text-sm text-card-foreground"
				>
					<div className="flex flex-col gap-1">
						<span className="font-semibold text-foreground">
							{isEn
								? "Ready-to-send application email"
								: "Email lamaran siap kirim"}
						</span>
						<span className="text-xs text-muted-foreground">
							{isEn
								? "Fill in the bracketed parts, then send it from your own inbox."
								: "Isi bagian dalam kurung, lalu kirim dari emailmu sendiri."}
						</span>
					</div>

					<div className="flex flex-col gap-1 rounded-lg bg-muted px-2.5 py-2 text-xs">
						<span className="text-muted-foreground">
							{isEn ? "To" : "Ke"}:{" "}
							<span className="font-mono text-foreground">
								{guestTemplate.to}
							</span>
						</span>
						<span className="text-muted-foreground">
							{isEn ? "Subject" : "Subjek"}:{" "}
							<span className="font-mono text-foreground">
								{guestTemplate.subject}
							</span>
						</span>
					</div>

					{/* Doubles as the copy-fallback selection target below. */}
					<textarea
						ref={guestTemplateBodyRef}
						readOnly
						rows={6}
						value={guestTemplate.body}
						aria-label={isEn ? "Email body" : "Isi email"}
						data-testid="apply-email-handoff-guest-template-body"
						className="w-full resize-none rounded-lg border border-border bg-background px-2.5 py-2 font-mono text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
					/>

					<div className="flex flex-col gap-1.5">
						<a
							href={guestTemplate.gmailUrl}
							target="_blank"
							rel="noopener noreferrer"
							onClick={onOpenGuestTemplateChannel("gmail")}
							data-testid="apply-email-handoff-guest-template-gmail"
							className={buttonClass({
								shape: "xl",
								fullWidth: true,
								className: "shadow-sm",
							})}
						>
							<Mail className="h-4 w-4" aria-hidden="true" />
							{isEn ? "Open in" : "Buka di"} Gmail
						</a>
						<a
							href={guestTemplate.mailtoUrl}
							onClick={onOpenGuestTemplateChannel("mailto")}
							data-testid="apply-email-handoff-guest-template-mailto"
							className="flex items-center justify-center gap-2 rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
						>
							{isEn ? "Open email app" : "Buka aplikasi email"}
						</a>
						<button
							type="button"
							onClick={() => void onCopyGuestTemplate()}
							data-testid="apply-email-handoff-guest-template-copy"
							className="flex items-center justify-center gap-1.5 rounded font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
						>
							<Copy className="h-3.5 w-3.5" aria-hidden="true" />
							{guestTemplateCopied
								? isEn
									? "Copied"
									: "Tersalin"
								: isEn
									? "Copy the email"
									: "Salin email"}
						</button>
					</div>

					<p className="text-xs text-muted-foreground">
						{isEn
							? "Attach your CV before sending."
							: "Lampirkan CV-mu sebelum kirim."}
					</p>

					<Link
						href={signInHref}
						data-testid="apply-email-handoff-guest-template-signin"
						className="self-start rounded text-xs font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
					>
						{isEn
							? "Sign in for a version tailored to your CV"
							: "Masuk untuk versi yang disesuaikan dengan CV-mu"}
					</Link>
				</div>
			);
		}

		return (
			<div
				data-testid="apply-email-handoff-blocked"
				className="flex flex-col gap-1.5 rounded-xl border border-border bg-card px-3 py-2.5 text-sm text-card-foreground"
			>
				<span className="flex items-start gap-2">
					<AlertTriangle
						className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
						aria-hidden="true"
					/>
					<span>
						{readiness.reason === "unauthorized"
							? isEn
								? "Log in and we'll write this application email for you."
								: "Masuk dan kami tuliskan email lamaran ini untukmu."
							: isEn
								? `Add ${missingDesc ?? "your name, email, and CV"} and we'll write this application email for you.`
								: `Lengkapi ${missingDesc ?? "nama, email, dan CV"} dan kami tuliskan email lamaran ini untukmu.`}
					</span>
				</span>
				{/* `callbackURL` is the CURRENT path, never a hand-built
				    `/jobs/<id>` — that shortcut is missing the slug every job URL
				    needs and 404s (src/lib/sitemap/CLAUDE.md). This blocker renders
				    on feed rows too, so the return target is wherever they are. */}
				<Link
					href={
						readiness.reason === "unauthorized"
							? signInHref
							: `/${locale}/settings`
					}
					data-testid="apply-email-handoff-blocked-fix"
					className="self-start rounded font-semibold text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
				>
					{readiness.reason === "unauthorized"
						? isEn
							? "Log in →"
							: "Masuk →"
						: isEn
							? "Complete profile →"
							: "Lengkapi profil →"}
				</Link>
			</div>
		);
	}

	return (
		<button
			type="button"
			onClick={onPrepare}
			disabled={pending}
			aria-busy={phase === "preparing"}
			data-testid="apply-email-handoff-cta"
			className={
				variant === "primary"
					? "flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3.5 text-base font-bold text-primary-foreground shadow-sm transition-all hover:bg-primary/90 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
					: "flex w-full items-center justify-center gap-2 rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-semibold text-foreground shadow-sm transition-colors hover:bg-muted disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
			}
		>
			{phase === "preparing" ? (
				<>
					<Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
					<span aria-live="polite">
						{prepStage === 0
							? isEn
								? "Tailoring your CV…"
								: "Menyesuaikan CV…"
							: prepStage === 1
								? isEn
									? "Writing your cover letter…"
									: "Menulis surat lamaran…"
								: isEn
									? "Still writing. This can take up to a minute."
									: "Masih menulis. Ini bisa sampai satu menit."}
					</span>
				</>
			) : (
				<>
					<Mail
						className={
							variant === "primary"
								? "h-4 w-4 text-primary-foreground"
								: "h-4 w-4 text-primary"
						}
						aria-hidden="true"
					/>
					{isEn
						? (label?.en ?? "Apply by email — we write it, you send it")
						: (label?.id ?? "Lamar via email — kami tulis, kamu kirim")}
				</>
			)}
		</button>
	);
}
